import { ReactNode, useState } from 'react';
import { css } from '@emotion/react';
import { Handle, Position } from '@xyflow/react';
import { CallRouteV1 } from '@frontend/api-call-route';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, InlineNotification, styles, Text, TextLink, Truncated } from '@frontend/design-system';
import { draggableStyles } from './styles';

type HandleOptions = {
  id: string;
};

type Handles = {
  top: HandleOptions;
  bottom: HandleOptions;
};

type Props = {
  nodeId: string;
  handles?: Handles;
  title: string;
  label: string;
  icon: IconName;
  type: CallRouteV1.Types.NodeType;
  linkId?: string;
  warning: string;
  canEdit?: boolean;
  chip?: ReactNode;
  additionalNotifications?: ReactNode;
  onClickEdit?: (data: { nodeId: string }) => void;
  onClickDelete?: (data: { nodeId: string }) => void;
  onCallObjectLinkClick?: () => void;
  onNodeExpanded?: (isExpanded: boolean) => void;
  children?: ReactNode;
};

export function BaseRoutingNode({
  handles = {
    top: { id: 'top' },
    bottom: { id: 'bottom' },
  },
  nodeId,
  icon,
  title,
  label,
  linkId,
  type,
  warning,
  canEdit,
  chip,
  additionalNotifications,
  onClickDelete,
  onClickEdit,
  onCallObjectLinkClick,
  onNodeExpanded,
  children,
  ...rest
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      css={[
        css`
          padding: ${theme.spacing(2)};
          border: 1px solid ${theme.colors.neutral20};
          border-radius: ${theme.borderRadius.medium};
          background: ${theme.colors.white};
          width: 320px;
        `,
        draggableStyles,
      ]}
      {...rest}
    >
      {handles.top && (
        <Handle
          type='target'
          position={Position.Top}
          id={handles.top.id}
          isConnectable={true}
          style={{ visibility: 'hidden' }}
        />
      )}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(1)};
        `}
      >
        {warning && <InlineNotification status='warn' size='small' message={warning} />}
        {additionalNotifications}

        <div
          css={css`
            display: grid;
            ${!label || label.trim() === ''
              ? `
                  grid-template-areas: 'icon title actions';
                  grid-template-rows: 1fr;
                  height: 40px;
                `
              : chip
              ? `
                    grid-template-areas: 'icon title actions' 'icon subtitle actions' 'icon chip actions';
                    grid-template-rows: auto auto auto;
                    height: auto;
                  `
              : `
                    grid-template-areas: 'icon title actions' 'icon subtitle actions';
                    grid-template-rows: auto auto;
                    height: 40px;
                  `}
            grid-template-columns: auto 1fr auto;
            column-gap: ${theme.spacing(1)};
            align-items: center;
            > * {
              min-width: 0;
            }
          `}
        >
          <Icon
            name={icon}
            css={css`
              grid-area: icon;
              color: ${theme.colors.neutral70};
            `}
          />
          <Text
            as='span'
            size='medium'
            weight='bold'
            css={[
              css`
                grid-area: title;
              `,
              styles.truncate,
            ]}
          >
            {title}
          </Text>
          {linkId && onCallObjectLinkClick ? (
            <Truncated
              textAlign='left'
              trackingId={`call-route-canvas-${type}-call-object-link`}
              as={TextLink}
              css={css`
                max-width: fit-content;
                grid-area: subtitle;
              `}
              onClick={onCallObjectLinkClick}
            >
              {label}
            </Truncated>
          ) : (
            <Truncated
              css={css`
                grid-area: subtitle;
              `}
              as='span'
            >
              {label}
            </Truncated>
          )}

          {chip && (
            <div
              css={css`
                grid-area: chip;
                margin-top: ${theme.spacing(1)};
              `}
            >
              {chip}
            </div>
          )}

          <div
            css={css`
              grid-area: actions;
            `}
          >
            {canEdit && (
              <>
                {onClickEdit && (
                  <Button
                    size='small'
                    variant='secondary'
                    iconName='edit-small'
                    css={css`
                      .button-icon {
                        color: ${theme.colors.neutral70};
                      }
                    `}
                    onClick={() => {
                      onClickEdit({ nodeId });
                    }}
                  />
                )}

                {onClickDelete && (
                  <Button
                    size='small'
                    variant='secondary'
                    iconName='trash-small'
                    css={css`
                      .button-icon {
                        color: ${theme.colors.neutral70};
                      }
                    `}
                    onClick={() => {
                      onClickDelete({ nodeId });
                    }}
                  />
                )}
              </>
            )}

            {children && (
              <Button
                size='small'
                variant='secondary'
                iconName='caret-down-small'
                trackingId={`call-route-canvas-${type}-expand`}
                css={css`
                  width: fit-content;
                  .button-icon {
                    transform: rotate(${isExpanded ? 180 : 0}deg);
                    color: ${theme.colors.neutral70};
                  }
                `}
                onClick={() => {
                  setIsExpanded((prev) => !prev);
                  onNodeExpanded?.(!isExpanded);
                }}
              />
            )}
          </div>
        </div>
      </div>
      {isExpanded && (
        <div
          css={css`
            border-top: 1px solid ${theme.colors.neutral20};
            margin-top: ${theme.spacing(2)};
            padding-top: ${theme.spacing(2)};
          `}
        >
          {children}
        </div>
      )}

      {handles.bottom && (
        <Handle
          type='source'
          position={Position.Bottom}
          id={handles.bottom.id}
          isConnectable={true}
          style={{ visibility: 'hidden' }}
        />
      )}
    </div>
  );
}
